import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import AnimalCard from '../../components/animal/AnimalCard';
import AnimalListItem from '../../components/animal/AnimalListItem';
import AnimalCardSkeleton from '../../components/animal/AnimalCardSkeleton';
import AnimalListItemSkeleton from '../../components/animal/AnimalListItemSkeleton';
import AnimalTableItem from '../../components/animal/AnimalTableItem';
import FilterDropdown, { FilterOption } from '../../components/custom-input/FilterDropdown';
import ToggleView from '../../components/toggle-view/ToggleView';
import { PlusCircleSvg, FilterIconSvg, CancelSVG } from '../../components/svgs/index';
import FilterInventoryModal from './filter-inventory-modal/FilterInventoryModal';
import AddInventoryModal from './add-inventory-modal/AddInventoryModal';
import { showToast } from '../../services/toast.service';
import { PermissionService } from '../../services/permission.service';
import { getSpecies } from '../../api/speciesApi';
import { getBreedsOfSpecies } from '../../api/breedsApi';
import { getAllAccountAnimals } from '../../api/animalsApi';
import { getInventoryCount } from '../../api/inventoryApi';
import { getSpecimenTypes } from '../../api/specimensTypesApi';
import { ViewMode } from '../../types/enums/viewMode.enum';
import { PagedResponse } from '../../types/interfaces/apiParams.interfaces';
import { SpecimenType } from '../../types/interfaces/specimen.interfaces';
import { Animal, Breed, Species } from '../../types/interfaces/animal.interfaces';
import { PromiseError, toastMessages } from '../../constants/errorMessages';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';

export interface filtersType {
  search: string;
  type: FilterOption<number | null>;
  species: FilterOption<number | null>;
  breed: FilterOption<number | null>;
  sort: FilterOption<string | null>;
}

export interface InventoryLocationState {
  filterPreset?: filtersType;
  scrollY?: number;
  pagingOffset?: number;
  viewType?: ViewMode;
  marketplaceRequiresAchSetup?: boolean;
  marketplaceRequiresDocumentUpload?: boolean;
  marketplacePayoutPending?: boolean;
}

export const pickInventoryListState = (state: unknown): InventoryLocationState => {
  const locationState = state as InventoryLocationState | null;
  return {
    filterPreset: locationState?.filterPreset,
    scrollY: locationState?.scrollY,
    pagingOffset: locationState?.pagingOffset,
    viewType: locationState?.viewType,
  };
};

export const defaultInventoryFilters: filtersType = {
  search: '',
  type: { name: '', value: null },
  species: { name: '', value: null },
  breed: { name: '', value: null },
  sort: { name: '', value: null },
};

type InventoryProps = {
  permissionService: PermissionService;
};

const Inventory: FC<InventoryProps> = ({ permissionService }): JSX.Element => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const locationState = state as InventoryLocationState | null;
  const filterPresetFromState = locationState?.filterPreset;
  const listRestoreRef = useRef<{ scrollY: number; pagingOffset: number } | null>(null);
  const hasCapturedRestoreRef = useRef(false);

  if (!hasCapturedRestoreRef.current) {
    hasCapturedRestoreRef.current = true;
    if (locationState?.scrollY != null && locationState?.pagingOffset != null) {
      listRestoreRef.current = { scrollY: locationState.scrollY, pagingOffset: locationState.pagingOffset };
    }
  }

  const pagingSize = 8;
  const minSearchLength = 2;

  const { accountId } = useAppSelector(state => state.activeAccount);
  const paymentStatus = useAppSelector(state => state.payment.addedPaymentMethod);
  const activeAccount = useAppSelector(state => state.activeAccount);
  const accountLocked = useAppSelector(state => state.activeAccount.locked);

  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevDebouncedSearchRef = useRef<string | null>(null);

  const [animals, setAnimals] = useState<Animal[]>([]);
  const [totalUnits, setTotalUnits] = useState<number>();
  const [pagingOffset, setPagingOffset] = useState<number>(0);
  const [pagingMax, setPagingMax] = useState<number>(0);
  const [isOpenAddInventoryModal, setIsOpenAddInventoryModal] = useState<boolean>(false);
  const [typeOptions, setTypeOptions] = useState<FilterOption<number | null>[]>([{ name: 'All', value: null }]);
  const [speciesOptions, setSpeciesOptions] = useState<FilterOption<number | null>[]>([{ name: 'All', value: null }]);
  const [breedOptions, setBreedOptions] = useState<FilterOption<number | null>[]>([{ name: 'All', value: null }]);
  const [isOpenFilterInventoryModal, setIsOpenFilterInventoryModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewType, setViewType] = useState<ViewMode>(locationState?.viewType ?? ViewMode.Card);
  const [scrollY, setScrollY] = useState<number>(locationState?.scrollY ?? 0);

  const [filters, setFilters] = useState<filtersType>(() => filterPresetFromState ?? defaultInventoryFilters);
  const [filterPreset, setFilterPreset] = useState<filtersType | undefined>(filterPresetFromState);
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    const search = filterPresetFromState?.search ?? defaultInventoryFilters.search;
    return search.length >= minSearchLength ? search : '';
  });

  const queryFilterKey = useMemo(
    () => [debouncedSearch, filters.type.value, filters.species.value, filters.breed.value, filters.sort.value].join('|'),
    [debouncedSearch, filters.type.value, filters.species.value, filters.breed.value, filters.sort.value],
  );

  const queryFilters = useMemo(
    (): filtersType => ({
      search: debouncedSearch,
      type: filters.type,
      species: filters.species,
      breed: filters.breed,
      sort: filters.sort,
    }),
    [debouncedSearch, filters.type, filters.species, filters.breed, filters.sort],
  );

  const sortOptions: FilterOption<string | null>[] = [
    { name: 'All', value: null },
    { name: 'Name (A-Z)', value: 'Name' },
    { name: 'Name (Z-A)', value: '^Name' },
    { name: 'Youngest', value: '^birthDate' },
    { name: 'Eldest', value: 'birthDate' },
  ];

  const resetScroll = useCallback(() => {
    setScrollY(0);
    window.scrollTo(0, 0);
    listRestoreRef.current = null;

    if (locationState?.scrollY != null || locationState?.pagingOffset != null) {
      const { scrollY: _scrollY, pagingOffset: _pagingOffset, ...restState } = locationState;
      navigate(pathname, { replace: true, state: restState });
    }
  }, [locationState, navigate, pathname]);

  useEffect(() => {
    setFilters(prev => {
      const type = typeOptions.find(option => option.value === prev.type.value) ?? prev.type;
      const species = speciesOptions.find(option => option.value === prev.species.value) ?? prev.species;
      const breed = breedOptions.find(option => option.value === prev.breed.value) ?? prev.breed;
      const sort = sortOptions.find(option => option.value === prev.sort.value) ?? prev.sort;

      if (
        type.name === prev.type.name &&
        species.name === prev.species.name &&
        breed.name === prev.breed.name &&
        sort.name === prev.sort.name
      ) {
        return prev;
      }

      return { ...prev, type, species, breed, sort };
    });
  }, [typeOptions, speciesOptions, breedOptions]);

  useEffect(() => {
    if (filterPresetFromState) {
      setFilters(filterPresetFromState);
      setFilterPreset(filterPresetFromState);
      setDebouncedSearch(filterPresetFromState.search.length >= minSearchLength ? filterPresetFromState.search : '');
    }
  }, [filterPresetFromState]);

  useEffect(() => {
    setFilterPreset(filters);
  }, [filters]);

  useEffect(() => {
    if (filters.search.length < minSearchLength) {
      setDebouncedSearch('');
      return;
    }

    const timeoutId = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  const handleGetSpecimenTypes = useCallback(async () => {
    try {
      const response = await getSpecimenTypes();
      const newType = response.data as SpecimenType[];
      setTypeOptions([
        { name: 'All', value: null },
        ...newType.map((specimenType: SpecimenType) => {
          return { name: specimenType.name!, value: specimenType.specimenTypeId };
        }),
      ]);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, []);

  const handleGetAllSpecies = useCallback(async () => {
    try {
      const response = await getSpecies();
      const newSpecies = response.data as Species[];
      setSpeciesOptions([
        { name: 'All', value: null },
        ...newSpecies.map((species: any) => {
          return { name: species.name, value: species.speciesId };
        }),
      ]);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, []);

  const handleGetBreedOptions = useCallback(async (speciesId: number) => {
    if (speciesId === null) {
      setBreedOptions([{ name: 'All', value: null }]);
    } else
      try {
        const response = await getBreedsOfSpecies(speciesId);
        const newBreeds = response.data as Breed[];
        setBreedOptions([
          { name: 'All', value: undefined },
          ...newBreeds.map((breeds: any) => {
            return { name: breeds.name, value: breeds.breedId };
          }),
        ]);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
  }, []);

  useEffect(() => {
    if (filters.species.value != null) {
      handleGetBreedOptions(filters.species.value);
    }
  }, [filters.species.value, handleGetBreedOptions]);

  useEffect(() => {
    handleGetSpecimenTypes();
  }, [handleGetSpecimenTypes]);

  useEffect(() => {
    handleGetAllSpecies();
  }, [handleGetAllSpecies]);

  useEffect(() => {
    getInventoryCount({ accountId: accountId })
      .then(e => {
        setTotalUnits(e.data);
      })
      .catch();
  }, []);

  useEffect(() => {
    const searchChanged = prevDebouncedSearchRef.current !== null && prevDebouncedSearchRef.current !== debouncedSearch;
    prevDebouncedSearchRef.current = debouncedSearch;

    if (searchChanged) {
      abortControllerRef.current?.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    isFetchingRef.current = false;

    const restore = listRestoreRef.current;
    if (restore) {
      listRestoreRef.current = null;
      void restoreListScroll(restore.pagingOffset, restore.scrollY, controller.signal);
    } else {
      setAnimals([]);
      setPagingOffset(0);
      resetScroll();
      void fetchItems(0, controller.signal);
    }
  }, [queryFilterKey]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      setScrollY(currentScrollY);

      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (
        scrollHeight - currentScrollY - clientHeight < 600 &&
        !isFetchingRef.current &&
        !isLoading &&
        pagingOffset < pagingMax
      ) {
        void fetchItems(pagingOffset, abortControllerRef.current?.signal);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoading, pagingOffset, pagingMax]);

  const restoreListScroll = async (targetOffset: number, scrollY: number, signal: AbortSignal) => {
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      let offset = 0;
      let accumulatedAnimals: Animal[] = [];
      let total = 0;

      while (offset < targetOffset) {
        if (signal.aborted) return;

        const response = await getAllAccountAnimals(
          accountId as number,
          {
            include: 'Breed.Species, Attachments',
            limit: pagingSize,
            offset,
            sort: queryFilters.sort.value ?? undefined,
            searchString: queryFilters.search || undefined,
            breedId: queryFilters.breed.value ?? undefined,
            speciesId: queryFilters.species.value ?? undefined,
            specimenTypeId: queryFilters.type.value ?? undefined,
          },
          { signal },
        );
        const pagedResponse: PagedResponse<Animal> = response.data as PagedResponse<Animal>;
        if (pagedResponse.result.length === 0) {
          break;
        }

        accumulatedAnimals = [...accumulatedAnimals, ...pagedResponse.result];
        total = pagedResponse.total;
        offset = Math.min(offset + pagingSize, pagedResponse.total);
      }

      if (signal.aborted) return;

      setAnimals(accumulatedAnimals);
      setPagingMax(total);
      setPagingOffset(offset);
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;

      const errorCode = error.code;
      if (errorCode === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else if (errorCode === PromiseError.BAD_REQUEST) {
        showToast.error(PromiseError.BAD_REQUEST);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
        isFetchingRef.current = false;
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      }
    }
  };

  const fetchItems = async (offset = pagingOffset, signal?: AbortSignal) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const response = await getAllAccountAnimals(
        accountId as number,
        {
          include: 'Breed.Species, Attachments',
          limit: pagingSize,
          offset: offset,
          sort: queryFilters.sort.value ?? undefined,
          searchString: queryFilters.search || undefined,
          breedId: queryFilters.breed.value ?? undefined,
          speciesId: queryFilters.species.value ?? undefined,
          specimenTypeId: queryFilters.type.value ?? undefined,
        },
        signal ? { signal } : undefined,
      );

      if (signal?.aborted) return;

      const pagedResponse: PagedResponse<Animal> = response.data as PagedResponse<Animal>;
      const newAnimals = pagedResponse.result;
      const newOffset = Math.min(offset + pagingSize, pagedResponse.total);

      setAnimals(prev => {
        if (offset === 0) return newAnimals;
        const existingIds = new Set(prev.map(a => a.animalId));
        const uniqueAnimals = newAnimals.filter(a => !existingIds.has(a.animalId));
        return [...prev, ...uniqueAnimals];
      });
      setPagingMax(pagedResponse.total);
      setPagingOffset(newOffset);
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;

      const errorCode = error.code;
      if (errorCode === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else if (errorCode === PromiseError.BAD_REQUEST) {
        showToast.error(PromiseError.BAD_REQUEST);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }
  };

  const checkPaymentValidity = () => {
    return (
      paymentStatus != undefined &&
      (!paymentStatus || activeAccount.billingAmount?.outstandingBalanceCents > 0 || activeAccount.billingAmount.penaltyCents > 0)
    );
  };

  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.type.value != null ||
    filters.species.value != null ||
    filters.breed.value != null ||
    filters.sort.value != null;

  const handleClearAll = () => {
    setFilters({ ...defaultInventoryFilters });
    setDebouncedSearch(defaultInventoryFilters.search);
    setFilterPreset(undefined);
    setBreedOptions([{ name: 'All', value: null }]);
    resetScroll();
  };

  const renderSkeletons = (count: number) =>
    Array.from({ length: count }).map((_, index) =>
      viewType === ViewMode.Card ? (
        <AnimalCardSkeleton key={`card-skeleton-${index}`} />
      ) : (
        <AnimalListItemSkeleton key={`list-skeleton-${index}`} />
      ),
    );

  const renderAnimalItem = (animal: Animal) => (
    <div key={`animal-card-id-${animal.animalId}`}>
      {viewType === ViewMode.Card ? (
        <AnimalCard
          animal={animal}
          filterPreset={filterPreset ?? filters}
          pagingOffset={pagingOffset}
          scrollY={scrollY}
          viewType={viewType}
        />
      ) : (
        <AnimalListItem
          animal={animal}
          filterPreset={filterPreset ?? filters}
          pagingOffset={pagingOffset}
          scrollY={scrollY}
          viewType={viewType}
        />
      )}
    </div>
  );

  return (
    <div>
      <div className="inventory">
        <div className="inventory-overview">
          <div>
            <h1>{PAGE_HEADER_CONSTANTS.MY_INVENTORY}</h1>
            <p>
              <span>{totalUnits}</span>
              {LABEL_CONSTANTS.TOTAL_UNITS}
            </p>
          </div>
          {permissionService.userHasInventoryPermission() && (
            <button
              className="button outlined"
              onClick={() => setIsOpenAddInventoryModal(true)}
              disabled={checkPaymentValidity() || accountLocked}>
              <PlusCircleSvg />
              <p>{BUTTON_CONSTANTS.ADD_INVENTORY}</p>
            </button>
          )}
        </div>
        <div className="inventory-search-row">
          <div className="inventory-search search-dropdown show-icon">
            <button className="mobile-view" onClick={() => setIsOpenFilterInventoryModal(true)}>
              <FilterIconSvg />
            </button>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button type="button" className="clear-all-button" onClick={handleClearAll}>
              <span>{BUTTON_CONSTANTS.CLEAR_ALL}</span>
              <CancelSVG />
            </button>
          )}
        </div>
        <div className="filters desktop-view">
          <div className="filter-option">
            <label>{LABEL_CONSTANTS.SPECIMEN_TYPE}</label>
            <FilterDropdown
              placeholder="All"
              options={typeOptions}
              value={filters.type}
              onChange={type => setFilters({ ...filters, type: type })}
            />
          </div>
          <div className="filter-option">
            <label>{ANIMAL_CONSTANTS.SPECIES}</label>
            <FilterDropdown
              placeholder="All"
              options={speciesOptions}
              value={filters.species}
              onChange={species => {
                handleGetBreedOptions(species.value);
                setFilters({ ...filters, breed: { name: '', value: null }, species: species });
              }}
            />
          </div>
          <div className="filter-option">
            <label>{ANIMAL_CONSTANTS.BREED}</label>
            <FilterDropdown
              placeholder="All"
              options={breedOptions}
              value={filters.breed}
              onChange={breed => setFilters({ ...filters, breed: breed })}
            />
          </div>
          <div className="filter-option">
            <label>{LABEL_CONSTANTS.SORT_BY}</label>
            <FilterDropdown
              placeholder="All"
              options={sortOptions}
              value={filters.sort}
              onChange={sort => setFilters({ ...filters, sort: sort })}
            />
          </div>
        </div>
        <div className="gallery">
          <div className="gallery-heading">
            <div className="gallery-heading-left">
              <h2>{LABEL_CONSTANTS.VIEWING}</h2>
              <p>
                {pagingOffset} of {pagingMax}
              </p>
            </div>
            <ToggleView value={viewType} onChange={setViewType} />
          </div>
          {viewType === ViewMode.Table ? (
            <div>
              <AnimalTableItem
                accountId={accountId}
                filters={queryFilters}
                filterPreset={filterPreset ?? filters}
                viewType={viewType}
                setPagingOffset={setPagingOffset}
              />
            </div>
          ) : !isLoading && animals?.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_INVENTORY}</div>
          ) : (
            <div className={`items ${viewType === ViewMode.List ? 'list-view' : 'card-view'}`}>
              {animals?.length === 0 ? renderSkeletons(pagingSize) : animals.map(renderAnimalItem)}
              {isLoading && animals?.length > 0 && renderSkeletons(pagingSize)}
            </div>
          )}
        </div>
      </div>
      {isOpenAddInventoryModal && <AddInventoryModal isOpen={isOpenAddInventoryModal} setIsOpen={setIsOpenAddInventoryModal} />}
      {isOpenFilterInventoryModal && (
        <FilterInventoryModal
          isOpen={isOpenFilterInventoryModal}
          setIsOpen={setIsOpenFilterInventoryModal}
          filters={filters}
          speciesOptions={speciesOptions}
          breedOptions={breedOptions}
          handleGetBreedOptions={handleGetBreedOptions}
          setFilters={setFilters}
          onClearAll={handleClearAll}
        />
      )}
    </div>
  );
};

export default Inventory;
